import { PropsWithChildren, useEffect, useState } from 'react';
import { Col, Container, Row } from 'styled-bootstrap-grid';
import styled from 'styled-components';
import { H1, H2 } from '../components/shared/titles';
import Skeleton from 'react-loading-skeleton';
import parse from 'html-react-parser';
import { getContent } from '../helpers/content-helper';
import { PAGES_CONTENT_ITEMS } from '../model/content-items';
import { Outlet } from 'react-router';
import { PageHead } from '../components/head/head';
import { BasePageContent } from '../model/content';
import { Headlines } from 'gocclr-shared-ui/dist/styles/styled/typography';
import { Medium } from '../styles/bootstrap-breakpoints';
import { useViewType } from '../hooks/use-view-type';
import { gridProps } from '../model/constants';

export interface LoginSubLayoutProps extends PropsWithChildren<object> {
}

export interface LoginSubLayoutContent extends BasePageContent {
    title: string;
    subTitle: string;
    summary: string;
    backgroundImage: string;
}

const LeftColumn = styled(Col).withConfig({
    shouldForwardProp: (prop) => !gridProps.includes(prop)
})`
    padding: 0 20px;
    color: ${props => props.theme.colors.text.white};
`;

const RightColumn = styled(Col).withConfig({
    shouldForwardProp: (prop) => !gridProps.includes(prop)
})`
    background-color: ${props => props.theme.colors.text.white};
    margin-top: 25px;

    @media (min-width: ${Medium}px) {
        margin-bottom: 25px;
    }
`;

const Title = styled(H1)`
    font-size: 50px;
    color: ${props => props.theme.colors.text.white};
    margin-top: 33px;
`;

const SpacerTop = styled.div`
    padding-top: 30px;

    p {
        font-size: 16px;
    }
`;

const SubTitle = styled(H2)`
    color: ${props => props.theme.colors.text.white};
    ${Headlines}
    font-size: 24px;
`;

const LoginImageContainer = styled.div<{ $backgroundImageUrl?: string; }>`
    background-image: url('${props => props.$backgroundImageUrl}');
    background-repeat: no-repeat;
    background-size: cover;
`;

const ContainerWithNoPadding = styled.div`
    width: 100%;
    padding-left: 0;
    padding-right: 0;
`;

export function LoginSubLayout(props: LoginSubLayoutProps) {
    const [content, setContent] = useState<LoginSubLayoutContent>();
    useEffect(() => {
        getContent<LoginSubLayoutContent>(PAGES_CONTENT_ITEMS.LOGIN).then(c => {
            setContent(c);
        })
    }, []);

    const viewType = useViewType();

    if (!content) {
        return (
            <ContainerWithNoPadding>
                <Container>
                    <Row>
                        <LeftColumn lg={7}>
                            <SpacerTop>
                                <Skeleton height={50} />
                            </SpacerTop>
                            <SpacerTop>
                                <Skeleton height={26} style={{ marginBottom: '16px' }} />
                                {viewType === 'desktop' && <>
                                    <br />
                                    <Skeleton height={24} style={{ marginBottom: '16px' }} />
                                    <Skeleton height={72} style={{ marginBottom: '16px' }} />
                                </>}
                            </SpacerTop>
                        </LeftColumn>
                        <RightColumn lg={4} lgOffset={1}>
                            <Skeleton height={400} />
                        </RightColumn>
                    </Row>
                </Container>
            </ContainerWithNoPadding>
        );
    }

    return (
        <PageHead {...content}>
            <ContainerWithNoPadding>
                <LoginImageContainer $backgroundImageUrl={content.backgroundImage} >
                    <Container>
                        <Row>
                            <LeftColumn lg={7}>
                                <SpacerTop>
                                    <Title>{content.title}</Title>
                                </SpacerTop>
                                <SpacerTop>
                                    <SubTitle>{content.subTitle}</SubTitle>
                                    {viewType === 'desktop' && (
                                        <>
                                            <br />
                                            {parse(content.summary)}
                                        </>
                                    )}
                                </SpacerTop>
                            </LeftColumn>
                            <RightColumn lg={4} lgOffset={1}>
                                <Outlet />
                                {props.children}
                            </RightColumn>
                        </Row>
                    </Container>
                </LoginImageContainer>
            </ContainerWithNoPadding>
        </PageHead>

    );
}